import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { Search, ChevronLeft, ChevronRight, Filter, User, Calendar, Activity, Eye, Edit, LogIn, FileText, Clock, RefreshCw } from 'lucide-react';
import BackButton from '../../components/BackButton';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const STAFF_ACTIVITY_COLLECTION = 'staff_activity';
const PAGE_SIZE = 50;

const ACTION_TYPE_LABELS = {
  login: { label: 'CRM Login', icon: LogIn, color: 'text-green-600' },
  account_view: { label: 'Account View', icon: Eye, color: 'text-blue-600' },
  note_added: { label: 'Note Added', icon: Edit, color: 'text-purple-600' },
  note_edited: { label: 'Note Edited', icon: Edit, color: 'text-purple-500' },
  stage_changed: { label: 'Stage Changed', icon: Activity, color: 'text-orange-600' },
  status_changed: { label: 'Status Changed', icon: Activity, color: 'text-orange-500' },
  hours_logged: { label: 'Hours Logged', icon: Clock, color: 'text-teal-600' },
  doc_uploaded: { label: 'Document Uploaded', icon: FileText, color: 'text-indigo-600' },
  account_updated: { label: 'Account Updated', icon: Edit, color: 'text-gray-600' },
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return format(date, 'MMM d, yyyy h:mm a');
}

function formatDateForInput(date) {
  return format(date, 'yyyy-MM-dd');
}

const StaffActivity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    userId: '',
    actionType: '',
    fromDate: formatDateForInput(subDays(new Date(), 7)),
    toDate: formatDateForInput(new Date()),
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadInternalUsers = async () => {
      try {
        const snap = await getDocs(collection(db, 'internal_users'));
        const users = snap.docs.map(doc => ({
          uid: doc.id,
          ...doc.data(),
          displayName: [doc.data().firstName, doc.data().lastName].filter(Boolean).join(' ') || doc.data().email || doc.id,
        }));
        users.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setInternalUsers(users);
      } catch (error) {
        console.error('Error loading internal users:', error);
      }
    };
    loadInternalUsers();
  }, []);

  const loadActivities = useCallback(async (resetPagination = true) => {
    setLoading(true);
    try {
      const constraints = [orderBy('timestamp', 'desc'), limit(PAGE_SIZE)];

      if (filters.userId) {
        constraints.unshift(where('actorUid', '==', filters.userId));
      }

      if (filters.actionType) {
        constraints.unshift(where('actionType', '==', filters.actionType));
      }

      if (filters.fromDate) {
        const fromTs = Timestamp.fromDate(startOfDay(new Date(filters.fromDate)));
        constraints.push(where('timestamp', '>=', fromTs));
      }

      if (filters.toDate) {
        const toTs = Timestamp.fromDate(endOfDay(new Date(filters.toDate)));
        constraints.push(where('timestamp', '<=', toTs));
      }

      if (!resetPagination && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, STAFF_ACTIVITY_COLLECTION), ...constraints);
      const snap = await getDocs(q);

      const items = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (resetPagination) {
        setActivities(items);
        setPage(1);
      } else {
        setActivities(prev => [...prev, ...items]);
      }

      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error loading staff activity:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, lastDoc]);

  useEffect(() => {
    loadActivities(true);
  }, [filters.userId, filters.actionType, filters.fromDate, filters.toDate]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(p => p + 1);
      loadActivities(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setLastDoc(null);
  };

  const handleRefresh = () => {
    setLastDoc(null);
    loadActivities(true);
  };

  const handleAccountClick = (accountId, route) => {
    if (route) {
      navigate(route);
    } else if (accountId) {
      navigate(`/crm/client/${accountId}`);
    }
  };

  const userMap = useMemo(() => {
    const map = {};
    internalUsers.forEach(u => {
      map[u.uid] = u;
    });
    return map;
  }, [internalUsers]);

  const getActorDisplay = (activity) => {
    const user = userMap[activity.actorUid];
    if (user) {
      return user.displayName;
    }
    return activity.actorName || activity.actorEmail || activity.actorUid || 'Unknown';
  };

  const actionTypes = Object.keys(ACTION_TYPE_LABELS);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PortalBackground />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton />
        </div>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Staff Activity Log</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Track CRM logins, account views, and changes by internal team members
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Staff Member
                </label>
                <select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Staff</option>
                  {internalUsers.map(user => (
                    <option key={user.uid} value={user.uid}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Activity className="w-4 h-4 inline mr-1" />
                  Action Type
                </label>
                <select
                  value={filters.actionType}
                  onChange={(e) => handleFilterChange('actionType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Actions</option>
                  {actionTypes.map(type => (
                    <option key={type} value={type}>
                      {ACTION_TYPE_LABELS[type].label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading && activities.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <BBALoader />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-20 text-gray-500 dark:text-gray-400">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No activity found for the selected filters.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Staff Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Account
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {activities.map((activity) => {
                      const actionMeta = ACTION_TYPE_LABELS[activity.actionType] || {
                        label: activity.actionType,
                        icon: Activity,
                        color: 'text-gray-600',
                      };
                      const Icon = actionMeta.icon;

                      return (
                        <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatTimestamp(activity.timestamp)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {getActorDisplay(activity)}
                                </div>
                                {activity.actorEmail && activity.actorEmail !== getActorDisplay(activity) && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {activity.actorEmail}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Icon className={`w-4 h-4 mr-2 ${actionMeta.color}`} />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {actionMeta.label}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-700 dark:text-gray-300 max-w-md truncate">
                              {activity.summary || '—'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {activity.accountId ? (
                              <button
                                onClick={() => handleAccountClick(activity.accountId, activity.route)}
                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                              >
                                {activity.accountId.slice(0, 8)}...
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}

              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
                Showing {activities.length} entries
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffActivity;
